// GAVION outlook-parity (review I4): weak stop wordings in lead replies wait for the rep. One-click
// « Confirmer le désabonnement » (= the link unsubscribe) / « Ignorer ». Pending requests older than 5 business
// days are highlighted (CASL: honour within 10 business days). Used on the lead page (one company), in the
// notifications menu (one request) and on the admin page (all pending).
import { useState, type ReactNode } from "react";
import {
  useGetList,
  useGetMany,
  useLocaleState,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { stopRequestDue } from "@/components/gavion/domain/email";
import { useGavionConfig } from "@/components/gavion/config/useGavionConfig";
import { cn } from "@/lib/utils";
import { toEmailErrorCode } from "../api/types";
import { useEmailApi } from "../api/useEmailApi";

const p = "gavion.email.delivery.stop_requests";

export type StopRequestRow = {
  id: Identifier;
  /** Null only once a decided request's reply was deleted (FK set null). */
  email_message_id: Identifier | null;
  sales_id: Identifier | null;
  company_id: Identifier | null;
  status: string;
  created_at: string;
};
type ReplyRow = {
  id: Identifier;
  from_email: string | null;
  subject: string | null;
};

/** The two decision buttons of one request. */
export const StopRequestActions = ({
  requestId,
  onDone,
}: {
  requestId: Identifier;
  onDone?: (status: string) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const api = useEmailApi();
  const [busy, setBusy] = useState(false);
  const decide = async (decision: "confirm" | "ignore") => {
    setBusy(true);
    try {
      const status = await api.emailDecideStopRequest(requestId, decision);
      notify(`${p}.${decision === "confirm" ? "confirmed" : "ignored"}`, {
        type: "success",
      });
      onDone?.(status);
      refresh();
    } catch (err) {
      notify(`gavion.email.common.errors.${toEmailErrorCode(err)}`, {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        size="sm"
        variant="destructive"
        disabled={busy}
        onClick={() => decide("confirm")}
      >
        {translate(`${p}.confirm`)}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => decide("ignore")}
      >
        {translate(`${p}.ignore`)}
      </Button>
    </div>
  );
};

const fmt = (iso: Date | string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-CA" : "fr-CA", {
    timeZone: "America/Toronto",
    dateStyle: "medium",
  }).format(new Date(iso));

/** Pending stop requests (all, or one company's), newest decisions first to act on. */
export const StopRequestList = ({
  companyId,
  showLeadLink = false,
  now = () => new Date(),
  empty = null,
}: {
  companyId?: Identifier;
  showLeadLink?: boolean;
  now?: () => Date;
  /** Rendered when nothing is pending (default: nothing). */
  empty?: ReactNode;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { config } = useGavionConfig();
  const { data: requests = [] } = useGetList<StopRequestRow>(
    "email_stop_requests",
    {
      filter: {
        status: "pending",
        ...(companyId != null ? { company_id: companyId } : {}),
      },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "ASC" },
    },
  );
  const { data: replies = [] } = useGetMany<ReplyRow>(
    "email_messages",
    {
      ids: requests
        .map((r) => r.email_message_id)
        .filter((x): x is Identifier => x != null),
    },
    { enabled: requests.length > 0 },
  );
  if (requests.length === 0) return <>{empty}</>;
  const replyOf = new Map(replies.map((m) => [String(m.id), m]));
  const at = now();
  return (
    <ul className="space-y-2" aria-label={translate(`${p}.title`)}>
      {requests.map((r) => {
        const reply = replyOf.get(String(r.email_message_id));
        const due = stopRequestDue(r.created_at, config.holidays);
        const overdue = at >= due.warnAt;
        return (
          <li
            key={r.id}
            data-testid={`stop-request-${r.id}`}
            data-overdue={overdue ? "true" : "false"}
            className={cn(
              "space-y-2 rounded-lg border p-3 text-sm",
              overdue ? "border-destructive bg-destructive/5" : "border-border",
            )}
          >
            <p className="font-medium">{reply?.from_email ?? "—"}</p>
            {reply?.subject ? (
              <p className="text-muted-foreground">
                {translate(`${p}.reply`, { subject: reply.subject })}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {translate(`${p}.received`, { date: fmt(r.created_at, locale) })}
              {" · "}
              {translate(`${p}.deadline`, { date: fmt(due.deadline, locale) })}
              {overdue ? (
                <span className="ml-2 font-semibold text-destructive">
                  {translate(`${p}.overdue`)}
                </span>
              ) : null}
            </p>
            {showLeadLink && r.company_id != null ? (
              <Link
                to={`/companies/${r.company_id}/show`}
                className="text-xs underline"
              >
                {translate(`${p}.lead`)}
              </Link>
            ) : null}
            <StopRequestActions requestId={r.id} />
          </li>
        );
      })}
    </ul>
  );
};
